import { useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import { AnimatedButton } from "../components/ui/AnimatedButton";
import { PageTransition } from "../components/ui/PageTransition";

export function LoginPage(): ReactNode {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justRegistered = searchParams.get("registered") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        Loading...
      </div>
    );
  }

  if (user !== null) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }

    setSubmitting(true);

    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <PageTransition>
        <div className="auth-card">
          <div className="auth-logo">prime</div>
          <p className="auth-subtitle">Sign in to your account</p>

          <form onSubmit={(e) => void handleSubmit(e)} noValidate>
            <AnimatePresence>
              {justRegistered && error === "" && (
                <motion.div
                  role="status"
                  aria-live="polite"
                  className="auth-success"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  Account created! Sign in to get started.
                </motion.div>
              )}
              {error !== "" && (
                <motion.div
                  role="alert"
                  aria-live="polite"
                  className="auth-error"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <AnimatedButton type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Logging in..." : "Log in"}
            </AnimatedButton>
          </form>

          <p className="auth-links">
            Don&apos;t have an account? <Link to="/signup">Sign up</Link>
          </p>
          <p className="auth-legal">
            <a href="/terms">Terms of Service</a> | <a href="/privacy">Privacy Policy</a>
          </p>
        </div>
      </PageTransition>
    </main>
  );
}
